/**
 * cameraWatch — "did the camera move enough to be worth a re-render?", in one place.
 *
 * Every view-dependent OGS consumer (appr is conditioned on a plücker ray, so its colours
 * are a function of the camera) needs the same question answered, and the epsilons must
 * agree: a pass costs an appr decode plus a lift.
 *
 * WHY A WATCH AND NOT A `__ORBIT_CONTROLS__` 'change' SUBSCRIPTION. That global is not a
 * stable object: it may not exist yet when a pipeline mounts, and its identity SWAPS on a
 * live↔edit flip (and on a camera rig appearing/disappearing in the live graph — drei's
 * OrbitControls ⇄ cameraRigCompat, Scene.jsx). A listener bound once therefore ends up on
 * an object nobody dispatches on any more, and the failure is SILENT: the camera still
 * moves, the splat just stops re-colouring. That is exactly the bug the eyes documented
 * (docs/OGS_EYES.md) and the same one the head/multi-region recolor hit in live mode.
 * A per-frame comparison against the live camera cannot go stale.
 */

import { vector3 } from './math/index.ts'
import type { CameraView, CameraLike } from './eyes/eyePass.ts'

// Below these a re-colour is not worth an appr decode plus a lift (docs/OGS_EYES.md).
export const CAMERA_MOVE_METRES = 2e-3
export const CAMERA_TURN_DOT = 1e-6

/** Did the camera move enough to be worth an appr decode + a lift? */
export function viewMoved(before: CameraView, after: CameraView): boolean {
  const moved = vector3.distance(before.origin, after.origin)
  if (moved > CAMERA_MOVE_METRES) return true
  if (before.fovDegrees !== after.fovDegrees) return true
  return 1 - vector3.dot(before.forward, after.forward) > CAMERA_TURN_DOT
}

/**
 * The same test, allocation-free, for a caller that asks EVERY FRAME.
 *
 * `viewMoved` above takes two `CameraView`s and so allocates three arrays per call — fine
 * for the eyes, which build a view per pass anyway, wrong for a `useFrame` watch. This keeps
 * the last accepted view in scalars and never allocates.
 *
 * `moved()` REMEMBERS the view it returned true at, so it reports a move once per move — call
 * it once per frame and act on the result. `reset()` forgets it, so the next call reports a
 * move (use it when something other than the camera invalidated the last pass).
 */
/** A camera already expressed in the avatar's frame — LocalFrameCamera's surface. */
export interface LocalView {
  position: { x: number; y: number; z: number }
  quaternion: { x: number; y: number; z: number; w: number }
  fov: number
}

export function createCameraWatch() {
  let have = false
  let px = 0, py = 0, pz = 0
  let fx = 0, fy = 0, fz = 0
  let fov = 0

  /** Compare against the last accepted view and, on a move, make this one the last. */
  const accept = (x: number, y: number, z: number, nx: number, ny: number, nz: number, nfov: number): boolean => {
    const changed = !have
      || Math.hypot(x - px, y - py, z - pz) > CAMERA_MOVE_METRES
      || nfov !== fov
      || 1 - (nx * fx + ny * fy + nz * fz) > CAMERA_TURN_DOT
    if (!changed) return false

    have = true
    px = x; py = y; pz = z
    fx = nx; fy = ny; fz = nz
    fov = nfov
    return true
  }

  return {
    moved(camera: CameraLike): boolean {
      camera.updateMatrixWorld?.()
      const e = camera.matrixWorld.elements
      // THREE's own convention: the view direction is the negated third column of matrixWorld.
      let nx = -e[8], ny = -e[9], nz = -e[10]
      const len = Math.hypot(nx, ny, nz) || 1
      nx /= len; ny /= len; nz /= len
      const { x, y, z } = camera.position
      return accept(x, y, z, nx, ny, nz, camera.fov ?? 45)
    },

    /** The same question for a camera in the AVATAR's frame: the avatar turning is a move too. */
    movedLocal(view: LocalView): boolean {
      const { x, y, z, w } = view.quaternion
      // (0, 0, -1) rotated by the quaternion — the forward `moved` reads off matrixWorld.
      const nx = -2 * (x * z + w * y)
      const ny = -2 * (y * z - w * x)
      const nz = -(1 - 2 * (x * x + y * y))
      return accept(view.position.x, view.position.y, view.position.z, nx, ny, nz, view.fov ?? 45)
    },

    reset(): void { have = false },
  }
}
